import React, { useState, Children } from 'react';
import { ArrowLeftIcon, PlusIcon, TrashIcon, AlertCircleIcon } from 'lucide-react';
interface AddRoomFormProps {
  onSave: (roomData: any) => void;
  onCancel: () => void;
}
export function AddRoomForm({
  onSave,
  onCancel
}: AddRoomFormProps) {
  const [roomData, setRoomData] = useState({
    name: '',
    category: '',
    code: '',
    description: '',
    isSmoking: false,
    isAccessible: false,
    isActive: false,
    view: '',
    size: '',
    amenities: [] as string[],
    baseAdultsOccupancy: 2,
    baseChildrenOccupancy: 0,
    maxOccupancy: 2,
    allowAdditionalAdults: false,
    allowAdditionalChildren: false,
    bedConfigurations: [{
      type: '',
      count: 1
    }]
  });
  const roomCategories = ['SUPERIOR', 'DELUXE', 'OVERWATER_VILLA', 'BEACH_VILLA', 'SUITE'];
  const roomViews = ['SEA_VIEW', 'GARDEN_VIEW', 'OCEAN_VIEW', 'LAGOON_VIEW', 'BEACH_VIEW'];
  const amenityOptions = ['Wi-Fi', 'TV', 'Minibar', 'Air Conditioning', 'Balcony', 'Terrace', 'Ocean View', 'Private Pool', 'Jacuzzi', 'Slippers', 'Desk', 'Dining Area', 'Interconnecting Rooms', 'Non-Smoking', 'Beach Access', 'Breakfast Included'];
  const bedTypes = ['King', 'Queen', 'Double', 'Twin', 'Single', 'Sofa Bed', 'Bunk Bed'];
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setRoomData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      checked
    } = e.target;
    setRoomData(prev => ({
      ...prev,
      [name]: checked
    }));
  };
  const handleNumberChange = (name: string, value: string) => {
    const numValue = value === '' ? 0 : parseInt(value, 10);
    setRoomData(prev => ({
      ...prev,
      [name]: numValue
    }));
  };
  const handleBedConfigChange = (index: number, field: string, value: string) => {
    const updatedConfigs = [...roomData.bedConfigurations];
    if (field === 'count') {
      updatedConfigs[index] = {
        ...updatedConfigs[index],
        [field]: parseInt(value, 10) || 1
      };
    } else {
      updatedConfigs[index] = {
        ...updatedConfigs[index],
        [field]: value
      };
    }
    setRoomData(prev => ({
      ...prev,
      bedConfigurations: updatedConfigs
    }));
  };
  const addBedConfig = () => {
    setRoomData(prev => ({
      ...prev,
      bedConfigurations: [...prev.bedConfigurations, {
        type: '',
        count: 1
      }]
    }));
  };
  const removeBedConfig = (index: number) => {
    const updatedConfigs = [...roomData.bedConfigurations];
    updatedConfigs.splice(index, 1);
    setRoomData(prev => ({
      ...prev,
      bedConfigurations: updatedConfigs
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(roomData);
  };
  return <div className="space-y-6" data-id="element-1626">
      <div className="flex items-center" data-id="element-1627">
        <button onClick={onCancel} className="flex items-center text-gray-600 hover:text-gray-900" data-id="element-1628">
          <ArrowLeftIcon size={18} className="mr-1" data-id="element-1629" />
          <span data-id="element-1630">Add Room</span>
        </button>
      </div>
      <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-1631">
        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-1632">
          <div data-id="element-1633">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1634">
              Room name<span className="text-red-500" data-id="element-1635">*</span>
            </label>
            <input type="text" id="name" name="name" value={roomData.name} onChange={handleInputChange} required className="w-full p-2 border border-gray-300 rounded-md" data-id="element-1636" />
          </div>
          <div data-id="element-1637">
            <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1638">
              Room category<span className="text-red-500" data-id="element-1639">*</span>
            </label>
            <div className="relative" data-id="element-1640">
              <select id="category" name="category" value={roomData.category} onChange={handleInputChange} required className="w-full p-2 border border-gray-300 rounded-md appearance-none pr-8" data-id="element-1641">
                <option value="" data-id="element-1642">Select room category</option>
                {roomCategories.map(category => <option key={category} value={category} data-id="element-1643">
                    {category.replace('_', ' ')}
                  </option>)}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none" data-id="element-1644">
                <svg className="w-4 h-4 fill-current text-gray-500" viewBox="0 0 20 20" data-id="element-1645">
                  <path d="M7 7l3-3 3 3m0 6l-3 3-3-3" strokeWidth="1.5" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round" data-id="element-1646" />
                </svg>
              </div>
            </div>
          </div>
        </div>
        <div className="mb-6" data-id="element-1647">
          <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1648">
            Room code
          </label>
          <input type="text" id="code" name="code" value={roomData.code} onChange={handleInputChange} placeholder="Enter room code" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-1649" />
        </div>
        <div className="mb-6" data-id="element-1650">
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1651">
            Description
          </label>
          <textarea id="description" name="description" value={roomData.description} onChange={handleInputChange} placeholder="Enter room description" rows={4} className="w-full p-2 border border-gray-300 rounded-md" data-id="element-1652" />
        </div>
        <div className="flex flex-wrap gap-6 mb-6" data-id="element-1653">
          <div className="flex items-center" data-id="element-1654">
            <input type="checkbox" id="isSmoking" name="isSmoking" checked={roomData.isSmoking} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-1655" />
            <label htmlFor="isSmoking" className="ml-2 text-sm text-gray-700" data-id="element-1656">
              Smoking
            </label>
          </div>
          <div className="flex items-center" data-id="element-1657">
            <input type="checkbox" id="isAccessible" name="isAccessible" checked={roomData.isAccessible} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-1658" />
            <label htmlFor="isAccessible" className="ml-2 text-sm text-gray-700" data-id="element-1659">
              Accessibility
            </label>
          </div>
          <div className="flex items-center" data-id="element-1660">
            <input type="checkbox" id="isActive" name="isActive" checked={roomData.isActive} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-1661" />
            <label htmlFor="isActive" className="ml-2 text-sm text-gray-700" data-id="element-1662">
              Status
            </label>
          </div>
        </div>
        {/* Room View and Size */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-1663">
          <div data-id="element-1664">
            <label htmlFor="view" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1665">
              Room view<span className="text-red-500" data-id="element-1666">*</span>
            </label>
            <div className="relative" data-id="element-1667">
              <select id="view" name="view" value={roomData.view} onChange={handleInputChange} required className="w-full p-2 border border-gray-300 rounded-md appearance-none pr-8" data-id="element-1668">
                <option value="" data-id="element-1669">Select room view</option>
                {roomViews.map(view => <option key={view} value={view} data-id="element-1670">
                    {view.replace('_', ' ')}
                  </option>)}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none" data-id="element-1671">
                <svg className="w-4 h-4 fill-current text-gray-500" viewBox="0 0 20 20" data-id="element-1672">
                  <path d="M7 7l3-3 3 3m0 6l-3 3-3-3" strokeWidth="1.5" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round" data-id="element-1673" />
                </svg>
              </div>
            </div>
          </div>
          <div data-id="element-1674">
            <label htmlFor="size" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1675">
              Room size (sq m)
            </label>
            <input type="number" id="size" name="size" value={roomData.size} onChange={handleInputChange} min="0" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-1676" />
          </div>
        </div>
        {/* Room Amenities */}
        <div className="mb-6" data-id="element-1677">
          <label htmlFor="amenities" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1678">
            Room amenities
          </label>
          <select id="amenities" name="amenities" multiple value={roomData.amenities} onChange={e => {
          const options = Array.from(e.target.selectedOptions, option => option.value);
          setRoomData(prev => ({
            ...prev,
            amenities: options
          }));
        }} className="w-full p-2 border border-gray-300 rounded-md" data-id="element-1679">
            {amenityOptions.map(amenity => <option key={amenity} value={amenity} data-id="element-1680">
                {amenity}
              </option>)}
          </select>
        </div>
        {/* Occupancy Settings */}
        <div className="mb-6" data-id="element-1681">
          <h3 className="text-md font-medium text-gray-800 mb-4" data-id="element-1682">
            Occupancy Settings
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4" data-id="element-1683">
            <div data-id="element-1684">
              <label htmlFor="baseAdultsOccupancy" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1685">
                Base Adults Occupancy<span className="text-red-500" data-id="element-1686">*</span>
              </label>
              <input type="number" id="baseAdultsOccupancy" value={roomData.baseAdultsOccupancy} onChange={e => handleNumberChange('baseAdultsOccupancy', e.target.value)} min="1" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-1687" />
              <p className="text-xs text-gray-500 mt-1" data-id="element-1688">
                Standard number of allowed adults for this room
              </p>
            </div>
            <div data-id="element-1689">
              <label htmlFor="baseChildrenOccupancy" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1690">
                Base Children Occupancy<span className="text-red-500" data-id="element-1691">*</span>
              </label>
              <input type="number" id="baseChildrenOccupancy" value={roomData.baseChildrenOccupancy} onChange={e => handleNumberChange('baseChildrenOccupancy', e.target.value)} min="0" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-1692" />
              <p className="text-xs text-gray-500 mt-1" data-id="element-1693">
                Standard number of allowed children for this room
              </p>
            </div>
            <div data-id="element-1694">
              <label htmlFor="maxOccupancy" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1695">
                Maximum Occupancy<span className="text-red-500" data-id="element-1696">*</span>
              </label>
              <input type="number" id="maxOccupancy" value={roomData.maxOccupancy} onChange={e => handleNumberChange('maxOccupancy', e.target.value)} min="1" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-1697" />
              <p className="text-xs text-gray-500 mt-1" data-id="element-1698">
                Maximum number of guests allowed
              </p>
            </div>
          </div>
          <div className="space-y-2 mb-4" data-id="element-1699">
            <div className="flex items-center" data-id="element-1700">
              <input type="checkbox" id="allowAdditionalAdults" name="allowAdditionalAdults" checked={roomData.allowAdditionalAdults} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-1701" />
              <label htmlFor="allowAdditionalAdults" className="ml-2 text-sm text-gray-700" data-id="element-1702">
                Allow additional adults
              </label>
            </div>
            <p className="text-xs text-gray-500 ml-6" data-id="element-1703">
              Allow additional adults (not exceeding total occupancy limit)
            </p>
            <div className="flex items-center mt-2" data-id="element-1704">
              <input type="checkbox" id="allowAdditionalChildren" name="allowAdditionalChildren" checked={roomData.allowAdditionalChildren} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-1705" />
              <label htmlFor="allowAdditionalChildren" className="ml-2 text-sm text-gray-700" data-id="element-1706">
                Allow additional children
              </label>
            </div>
            <p className="text-xs text-gray-500 ml-6" data-id="element-1707">
              Allow additional children (not exceeding total occupancy limit)
            </p>
          </div>
          {/* Occupancy Summary */}
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4" data-id="element-1708">
            <div className="flex" data-id="element-1709">
              <AlertCircleIcon className="h-5 w-5 text-red-500 mr-2" data-id="element-1710" />
              <div data-id="element-1711">
                <p className="text-sm text-red-700" data-id="element-1712">
                  This room has a base occupancy of{' '}
                  {roomData.baseAdultsOccupancy} Adults and{' '}
                  {roomData.baseChildrenOccupancy} Children with a total maximum
                  occupancy of {roomData.maxOccupancy}.
                </p>
                <p className="text-sm text-red-700" data-id="element-1713">
                  Additional adults are not allowed.
                </p>
                <p className="text-sm text-red-700" data-id="element-1714">
                  Additional children are not allowed.
                </p>
                <p className="text-sm text-red-700" data-id="element-1715">
                  Additional cost per child or adults on top of the base
                  occupancy can be configured in rates.
                </p>
              </div>
            </div>
          </div>
        </div>
        {/* Bed Configuration */}
        <div className="mb-6" data-id="element-1716">
          <h3 className="text-md font-medium text-gray-800 mb-4" data-id="element-1717">
            Bed configuration<span className="text-red-500" data-id="element-1718">*</span>
          </h3>
          {roomData.bedConfigurations.map((config, index) => <div key={index} className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 items-end" data-id="element-1719">
              <div data-id="element-1720">
                <label htmlFor={`bedType-${index}`} className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1721">
                  Bed type
                </label>
                <div className="relative" data-id="element-1722">
                  <select id={`bedType-${index}`} value={config.type} onChange={e => handleBedConfigChange(index, 'type', e.target.value)} className="w-full p-2 border border-gray-300 rounded-md appearance-none pr-8" data-id="element-1723">
                    <option value="" data-id="element-1724">Select bed type</option>
                    {bedTypes.map(type => <option key={type} value={type} data-id="element-1725">
                        {type}
                      </option>)}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none" data-id="element-1726">
                    <svg className="w-4 h-4 fill-current text-gray-500" viewBox="0 0 20 20" data-id="element-1727">
                      <path d="M7 7l3-3 3 3m0 6l-3 3-3-3" strokeWidth="1.5" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round" data-id="element-1728" />
                    </svg>
                  </div>
                </div>
              </div>
              <div className="flex items-center" data-id="element-1729">
                <div className="flex-grow" data-id="element-1730">
                  <label htmlFor={`bedCount-${index}`} className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1731">
                    Bed count
                  </label>
                  <input type="number" id={`bedCount-${index}`} value={config.count} onChange={e => handleBedConfigChange(index, 'count', e.target.value)} min="1" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-1732" />
                </div>
                {index > 0 && <button type="button" onClick={() => removeBedConfig(index)} className="ml-2 mt-6 p-2 text-red-600 hover:text-red-800" data-id="element-1733">
                    <TrashIcon size={18} data-id="element-1734" />
                  </button>}
              </div>
            </div>)}
          <button type="button" onClick={addBedConfig} className="flex items-center text-blue-600 hover:text-blue-800" data-id="element-1735">
            <PlusIcon size={16} className="mr-1" data-id="element-1736" />
            Add bed configuration
          </button>
        </div>
        {/* Submit Button */}
        <div className="flex justify-end" data-id="element-1737">
          <button type="submit" className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700" data-id="element-1738">
            Save
          </button>
        </div>
      </form>
    </div>;
}